import { ref } from 'vue';
import { visitsAPI, type Visit, type VisitsFilters } from '../services/api';
import { getI18nT } from '../i18n';

const visits = ref<Visit[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const currentPage = ref(1);
const totalCount = ref(0);
const hasMore = ref(true);
const currentFilters = ref<VisitsFilters>({});

/** What a mutation hands back to whoever triggered it. */
export type MutationResult<T> = { ok: true; data: T } | { ok: false; message: string };

// The API error handler answers with `{ error }` on every path; `{ message }` is
// kept as a fallback for anything that does not come from it.
export const serverMessage = (err: any): string | undefined =>
  err?.response?.data?.error || err?.response?.data?.message;

const failure = (err: any, fallbackKey: string): MutationResult<never> => ({
  ok: false,
  message: serverMessage(err) || getI18nT()(fallbackKey),
});

export function useVisits() {
  const fetchVisits = async (page = 1, limit = 10, filters?: VisitsFilters): Promise<boolean> => {
    if (filters !== undefined) {
      currentFilters.value = filters;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const res = await visitsAPI.getAll(page, limit, currentFilters.value);
      const { data, total } = res.data;

      visits.value = page === 1 ? data : [...visits.value, ...data];
      currentPage.value = page;
      totalCount.value = total;
      hasMore.value = visits.value.length < total;

      return true;
    } catch (err: any) {
      error.value = serverMessage(err) || getI18nT()('composables.visits.error_fetch');
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  // The mutations deliberately leave `isLoading` and `error` alone: those two are
  // the list's own fetch state, and flipping isLoading would swap every row for the
  // loading placeholder mid-cancel. Each caller renders the returned outcome itself.
  const rescheduleVisit = async (id: string, visitAt: string): Promise<MutationResult<Visit>> => {
    try {
      const { data } = await visitsAPI.reschedule(id, visitAt);

      // Patched in place rather than refetched, so the list keeps its scroll
      // position and any pages already loaded.
      const index = visits.value.findIndex((visit) => visit.id === id);
      if (index !== -1) {
        visits.value[index] = { ...visits.value[index], ...data };
      }

      return { ok: true, data };
    } catch (err: any) {
      return failure(err, 'composables.visits.error_reschedule');
    }
  };

  const cancelVisit = async (id: string): Promise<MutationResult<null>> => {
    try {
      await visitsAPI.cancel(id);

      const index = visits.value.findIndex((visit) => visit.id === id);
      if (index !== -1) {
        visits.value.splice(index, 1);
        totalCount.value = Math.max(0, totalCount.value - 1);
      }

      return { ok: true, data: null };
    } catch (err: any) {
      return failure(err, 'composables.visits.error_cancel');
    }
  };

  const clearVisits = () => {
    visits.value = [];
    currentPage.value = 1;
    totalCount.value = 0;
    hasMore.value = true;
    currentFilters.value = {};
  };

  return {
    visits,
    isLoading,
    error,
    currentPage,
    totalCount,
    hasMore,
    currentFilters,
    fetchVisits,
    rescheduleVisit,
    cancelVisit,
    clearVisits,
  };
}
